// Telegram native MainButton / BackButton hooks — Phase 12 Plan 12-07.
//
// UI-SPEC §Platform Contract + PATTERNS §Telegram Platform Contract:
//   - Primary CTA → нативная MainButton (НЕ floating-кнопка): setLoading +
//     disabled (Pitfall 5, защита от double-tap), cleanup on unmount, один
//     источник на экран.
//   - Вложенные экраны → BackButton; скрывать на корне (unmount on cleanup).
//
// sdk-react 3.3.9 exposes `mainButton` / `backButton` как scoped imperative
// объекты, методы которых — SafeWrapped с `.isAvailable()`. Вне Telegram
// (jsdom / dev / desktop без поддержки) `.isAvailable()` === false → все вызовы
// no-op. Поэтому каждый вызов гардим — хук безопасно деградирует в тестах и в
// браузере вне Telegram (экраны держат собственный in-DOM fallback с testid).

import { useEffect } from "react";

import { backButton, mainButton } from "@telegram-apps/sdk-react";

/** Безопасно вызвать SafeWrapped-метод SDK (no-op вне Telegram). */
function safeCall(fn: unknown, ...args: unknown[]): void {
  if (typeof fn !== "function") return;
  const wrapped = fn as ((...a: unknown[]) => unknown) & {
    isAvailable?: () => boolean;
  };
  try {
    if (typeof wrapped.isAvailable === "function" && !wrapped.isAvailable()) {
      return;
    }
    wrapped(...args);
  } catch {
    // SDK не инициализирован (jsdom / вне Telegram) — тихо игнорируем.
  }
}

/** Подписаться на SafeWrapped onClick, вернуть off-функцию (или no-op). */
function safeOnClick(fn: unknown, handler: () => void): () => void {
  if (typeof fn !== "function") return () => {};
  const wrapped = fn as ((h: () => void) => unknown) & {
    isAvailable?: () => boolean;
  };
  try {
    if (typeof wrapped.isAvailable === "function" && !wrapped.isAvailable()) {
      return () => {};
    }
    const off = wrapped(handler);
    return typeof off === "function" ? (off as () => void) : () => {};
  } catch {
    return () => {};
  }
}

/**
 * True when the native Telegram MainButton is usable (real Telegram client).
 * Screens use this to HIDE their in-DOM primary-CTA fallback so the action is
 * not duplicated (UI-SPEC §87: native MainButton is the only primary CTA).
 * Outside Telegram (web/desktop/jsdom) → false → the in-DOM fallback renders.
 */
export function isMainButtonAvailable(): boolean {
  try {
    const wrapped = mainButton.mount as unknown as { isAvailable?: () => boolean };
    return typeof wrapped?.isAvailable === "function" && wrapped.isAvailable();
  } catch {
    return false;
  }
}

export interface MainButtonOptions {
  /** Текст кнопки (видимый label primary CTA). */
  text: string;
  /** Обработчик нажатия. */
  onClick: () => void;
  /** Loading-спиннер (Pitfall 5 — защита от double-tap во время submit). */
  loading?: boolean;
  /** Активна ли кнопка (disabled блокирует повторный submit). */
  enabled?: boolean;
  /** Показать кнопку. По умолчанию true. */
  visible?: boolean;
}

/**
 * Нативная Telegram MainButton как primary CTA экрана. Один источник на экран
 * (UI-SPEC §6). Mount при первом рендере, unmount при размонтировании, params
 * пересчитываются на каждое изменение text/loading/enabled/visible.
 */
export function useMainButton({
  text,
  onClick,
  loading = false,
  enabled = true,
  visible = true,
}: MainButtonOptions): void {
  // Mount / unmount lifecycle (один раз на экран).
  useEffect(() => {
    safeCall(mainButton.mount);
    return () => {
      safeCall(mainButton.setParams, { isVisible: false });
      safeCall(mainButton.unmount);
    };
  }, []);

  // Параметры (text / loading / enabled / visible) — пересчёт при изменении.
  useEffect(() => {
    safeCall(mainButton.setParams, {
      text,
      isVisible: visible,
      isEnabled: enabled && !loading,
      isLoaderVisible: loading,
    });
  }, [text, loading, enabled, visible]);

  // Подписка на click — пересоздаётся при смене handler.
  useEffect(() => {
    const off = safeOnClick(mainButton.onClick, onClick);
    return off;
  }, [onClick]);
}

/**
 * Нативная Telegram BackButton для вложенного экрана (TripDetails / NewTrip).
 * Показывается на mount, скрывается + отписывается на unmount (корневые вкладки
 * остаются без BackButton).
 */
export function useBackButton(onClick: () => void): void {
  useEffect(() => {
    safeCall(backButton.mount);
    safeCall(backButton.show);
    const off = safeOnClick(backButton.onClick, onClick);
    return () => {
      off();
      safeCall(backButton.hide);
      safeCall(backButton.unmount);
    };
  }, [onClick]);
}
